import { ChangeDetectionStrategy, Component, ElementRef, HostListener, OnDestroy, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { BundleType, EntityTypeId, FieldName } from '../../models';
import { ProfileService } from '../../services/profile.service';

export type SocialWallAction =
  | { type: 'like'; item: any; reply?: any }
  | { type: 'pin'; item: any }
  | { type: 'reply'; item: any; parentReplyId: string | null; text: string }
  | { type: 'loadComments'; item: any; parentCommentId?: string | null; offset?: number }
  | { type: 'openLinkedContent'; content: any };

export interface SocialWallDetailDialogData {
  items: () => any[];
  activeIndex: number;
}

type SocialWallMediaType = 'image' | 'video' | 'audio';

const MEDIA_FIELDS: { field: string, type: SocialWallMediaType }[] = [
  { field: 'mediaImage', type: 'image' },
  { field: 'mediaVideo', type: 'video' },
  { field: 'mediaAudio', type: 'audio' }
];

@Component({
  selector: 'lib-social-wall-detail-dialog',
  templateUrl: './social-wall-detail-dialog.component.html',
  styleUrl: './social-wall-detail-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SocialWallDetailDialogComponent implements OnDestroy {

  public readonly action = new Subject<SocialWallAction>();

  public readonly entityTypeId = EntityTypeId;
  public readonly bundleType = BundleType;
  public readonly fieldName = FieldName;

  private readonly data = inject<SocialWallDetailDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<SocialWallDetailDialogComponent>);
  private readonly profileService = inject(ProfileService);

  public items = computed<any[]>(() => this.data?.items?.() ?? []);
  public activeIndex = signal(this.data?.activeIndex ?? 0);

  public item = computed<any>(() => this.items()[this.activeIndex()]);
  public replies = computed<any[]>(() => this.item()?.replies ?? []);
  public repliesCount = computed<number>(() => this.item()?.commentsAmount ?? this.replies().length);
  public canGoPrevious = computed(() => this.activeIndex() > 0);
  public canGoNext = computed(() => this.activeIndex() < this.items().length - 1);

  public isLoadingMoreReplies = signal(false);
  public isSubmittingReply = signal(false);
  public hasMoreReplies = computed(() => {
    const total = this.item()?.topCommentsTotal;
    return total == null ? false : this.replies().length < total;
  });

  public media = computed<{ type: SocialWallMediaType, value: any } | null>(() => {
    const item = this.item();
    const media = MEDIA_FIELDS.find(({ field }) => !!item?.[field]);

    return media ? { type: media.type, value: item[media.field] } : null;
  });

  public replyText = signal('');
  public replyTarget = signal<any | null>(null);

  public profile$ = this.profileService.getCurrentProfile();

  private replyField = viewChild<ElementRef<HTMLInputElement>>('replyField');
  private repliesScroll = viewChild<ElementRef<HTMLElement>>('repliesScroll');
  private contentScroll = viewChild<ElementRef<HTMLElement>>('contentScroll');

  constructor() {
    effect(() => {
      this.activeIndex();

      const item = untracked(() => this.item());

      if (item) {
        this.action.next({ type: 'loadComments', item, parentCommentId: null });
      }
    });

    effect(() => {
      this.replies();
      this.isLoadingMoreReplies.set(false);

      if (untracked(() => this.isSubmittingReply())) {
        this.isSubmittingReply.set(false);
        this.clearReply();
      }
    });
  }

  ngOnDestroy(): void {
    this.action.complete();
  }

  @HostListener('document:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if (this.isTypingTarget(event.target)) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      this.goTo(-1);
    }

    if (event.key === 'ArrowRight') {
      this.goTo(1);
    }
  }

  public goTo(step: number): void {
    const index = this.activeIndex() + step;

    if (index < 0 || index >= this.items().length) {
      return;
    }

    this.activeIndex.set(index);
    this.clearReply();

    // Either the whole sheet scrolls (phone) or just the replies (desktop).
    [this.contentScroll(), this.repliesScroll()].forEach(container => {
      if (container) {
        container.nativeElement.scrollTop = 0;
      }
    });
  }

  public close(): void {
    this.dialogRef.close(this.activeIndex());
  }

  public toggleLike(reply?: any): void {
    this.action.next({ type: 'like', item: this.item(), reply });
  }

  public togglePin(): void {
    this.action.next({ type: 'pin', item: this.item() });
  }

  public openLinkToContent(content: any): void {
    this.action.next({ type: 'openLinkedContent', content });
  }

  public loadReplies(reply: any): void {
    this.action.next({ type: 'loadComments', item: this.item(), parentCommentId: reply?.id });
  }

  public onRepliesScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const distanceToBottom = element.scrollHeight - element.offsetHeight - element.scrollTop;

    if (distanceToBottom < 150) {
      this.loadMoreReplies();
    }
  }

  private loadMoreReplies(): void {
    if (this.isLoadingMoreReplies() || !this.hasMoreReplies()) {
      return;
    }

    this.isLoadingMoreReplies.set(true);
    this.action.next({ type: 'loadComments', item: this.item(), parentCommentId: null, offset: this.replies().length });
  }

  public replyTo(reply: any): void {
    this.replyTarget.set(reply);
    this.replyField()?.nativeElement.focus();
  }

  public clearReply(): void {
    this.replyText.set('');
    this.replyTarget.set(null);
  }

  public submitReply(): void {
    const text = this.replyText().trim();

    if (!text || this.isSubmittingReply()) {
      return;
    }

    this.isSubmittingReply.set(true);

    this.action.next({
      type: 'reply',
      item: this.item(),
      parentReplyId: this.replyTarget()?.id ?? null,
      text
    });
  }

  private isTypingTarget(target: EventTarget | null): boolean {
    const element = target as HTMLElement;

    return !!element?.closest?.('input, textarea, [contenteditable="true"]');
  }

}
